import React from 'react';
import { useHistoricalData } from '../hooks/useHistoricalData';

interface HistoricalDataSelectorProps {
  className?: string;
  isDarkMode?: boolean;
}

export const HistoricalDataSelector: React.FC<HistoricalDataSelectorProps> = ({ 
  className = '', 
  isDarkMode = false 
}) => {
  const { 
    selectedDate, 
    setSelectedDate, 
    selectedTimestamp,
    setSelectedTimestamp,
    availableDates, 
    availableTimestamps,
    isLoading, 
    error,
    fetchAvailableDates,
    fetchAvailableTimestamps,
    currentTradingDate
  } = useHistoricalData();

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedDate(value === 'current' ? null : value);
  };

  const handleTimestampChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedTimestamp(value === 'latest' ? null : value);
  };

  const handleRefresh = () => {
    fetchAvailableDates();
    if (selectedDate) {
      fetchAvailableTimestamps(selectedDate);
    }
  };

  const formatDate = (dateStr: string) => {
    // Convert YYYYMMDD to readable format
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${month}/${day}/${year}`;
  };

  const getCurrentDate = () => {
    return currentTradingDate;
  };

  const currentDateStr = getCurrentDate();

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <label className="text-xs font-medium" style={{ color: 'var(--muted)' }} title="Data before US market open (9:30 AM ET) is considered previous trading day">
        Trading Date:
      </label>
      
      <select
        value={selectedDate || 'current'}
        onChange={handleDateChange}
        disabled={isLoading}
        className={`text-xs px-2 py-1 rounded border ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={{ background: 'var(--elev)', borderColor: 'var(--border)', color: 'var(--txt)' }}
      >
        <option value="current">Current ({formatDate(currentDateStr)})</option>
        {availableDates.map(date => (
          <option key={date} value={date}>
            {formatDate(date)}
          </option>
        ))}
      </select>

      {/* Timestamp selector - only show when a historical date is selected */}
      {selectedDate && availableTimestamps.length > 0 && (
        <>
          <label className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
            Time:
          </label>
          <select
            value={selectedTimestamp || 'latest'}
            onChange={handleTimestampChange}
            disabled={isLoading}
            className={`text-xs px-2 py-1 rounded border ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={{ background: 'var(--elev)', borderColor: 'var(--border)', color: 'var(--txt)' }}
          >
            <option value="latest">Latest</option>
            {availableTimestamps.map(timestamp => (
              <option key={timestamp} value={timestamp}>
                {timestamp}
              </option>
            ))}
          </select>
        </>
      )}

      <button
        onClick={handleRefresh}
        disabled={isLoading}
        className={`mmec-btn ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="Refresh available dates and timestamps"
      >
        {isLoading ? '...' : '↻'}
      </button>

      {error && (
        <span className="text-xs" style={{ color: 'var(--down)' }}>
          {error}
        </span>
      )}

      {selectedDate && (
        <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--acc-dim)', color: 'var(--acc)', fontWeight: 500 }}>
          Historical Mode {selectedTimestamp && `@ ${selectedTimestamp}`}
        </span>
      )}
    </div>
  );
};
